import {useMutation, useQuery} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";

const apiUrl = "api/daily-report";

export interface SaveDailyReportDiagnosisPayload {
    powerStationId: number;
    dateTime: string;
    description: string;
}

export interface DailyReportDiagnosisResponse {
    id: number;
    powerStationId: number;
    dateTime: string;
    description: string;
    updatedAt: string;
}

const appendPowerStationIds = (params: URLSearchParams, powerStationIds?: number[] | null) => {
    if (!powerStationIds || powerStationIds.length === 0) return;
    powerStationIds.forEach((id) => params.append("powerStationIds", String(id)));
};

const fetchDailyReport = async (
    pagedSearchParams: PagedSearchParams,
    dateTime: string | null,
    powerStationIds?: number[] | null
) => {
    try {
        const params = pagedSearchParams ? pagedSearchParams.toURLSearchParams() : new URLSearchParams();
        if (dateTime) {
            params.append("dateTime", dateTime);
        }
        appendPowerStationIds(params, powerStationIds);
        return await axios
            .get(apiUrl, {
                params,
                timeout: 30000,
            })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching daily report:", error);
        return Promise.reject(error);
    }
};

export const DailyReportReactQuery = (
    pagedSearchParams: PagedSearchParams,
    dateTime: string | null,
    powerStationIds?: number[] | null
) => {
    return useQuery({
        queryKey: ["daily-report", pagedSearchParams, dateTime, powerStationIds],
        queryFn: () => fetchDailyReport(pagedSearchParams, dateTime, powerStationIds),
        enabled: !!dateTime,
    });
};

const exportDailyReport = async (
    format: "csv" | "excel",
    pagedSearchParams: PagedSearchParams,
    dateTime: string | null,
    powerStationIds?: number[] | null
) => {
    const params = pagedSearchParams ? pagedSearchParams.toURLSearchParams() : new URLSearchParams();
    if (dateTime) {
        params.append("dateTime", dateTime);
    }
    appendPowerStationIds(params, powerStationIds);
    const formatPath = format === "excel" ? "xlsx" : "csv";
    const response = await axios.post(`${apiUrl}/export/${formatPath}`, null, {
        params,
        responseType: "blob",
        timeout: 60000,
    });

    const ext = format === "csv" ? "csv" : "xlsx";
    const isoDate = dateTime ? dateTime.substring(0, 10) : new Date().toISOString().substring(0, 10);
    const [yyyy, mm, dd] = isoDate.split("-");
    const datePart = `${dd}-${mm}-${yyyy}`;
    let filename = `report_diario_${datePart}.${ext}`;
    const contentDisposition = response.headers["content-disposition"] as string | undefined;
    if (contentDisposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(contentDisposition);
        if (match?.[1]) {
            filename = decodeURIComponent(match[1]);
        }
    }

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const ExportDailyReportReactQuery = () => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    return useMutation({
        mutationFn: (payload: {
            format: "csv" | "excel";
            pagedSearchParams: PagedSearchParams;
            dateTime: string | null;
            powerStationIds?: number[] | null;
        }) => exportDailyReport(payload.format, payload.pagedSearchParams, payload.dateTime, payload.powerStationIds),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Exportação concluída com sucesso!", {variant: "success"});
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao exportar acompanhamento diário: ${error?.message ?? "Erro desconhecido"}`, {variant: "error"});
        },
    });
};

const saveDailyReportDiagnosis = async (payload: SaveDailyReportDiagnosisPayload): Promise<DailyReportDiagnosisResponse> => {
    const response = await axios.put(`${apiUrl}/diagnosis`, payload, {timeout: 30000});
    return response.data;
};

export const SaveDailyReportDiagnosisReactQuery = () => {
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();

    return useMutation({
        mutationFn: (payload: SaveDailyReportDiagnosisPayload) => saveDailyReportDiagnosis(payload),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Diagnóstico salvo com sucesso!", {variant: "success"});
        },
        onError: (error: any) => {
            closeSnackbar();
            const detail = error?.response?.data?.detail
                ?? error?.message
                ?? "Erro desconhecido";
            enqueueSnackbar(`Erro ao salvar diagnóstico: ${detail}`, {variant: "error"});
        },
    });
};
